import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { FormattedMessage } from 'react-intl';
import Layout from '../components/Layout';
import LocalizedLink from '../components/LocalizedLink';
import HeadMeta from '../components/HeadMeta';
import { fillFigures } from '../utils/keyFigures';
import {
  LinkedinShareButton,
  TwitterShareButton,
  EmailShareButton
} from 'react-share';

const BlogPost = ({ pageContext: { locale }, data }) => {
  const post = data.markdownRemark;
  // {{key}} placeholders in articles are filled from src/data/keyFigures.json, in the article's language
  const articleLang = (post.frontmatter.language || 'fr').toLowerCase().startsWith('en') ? 'en' : 'fr';
  const html = fillFigures(post.html, articleLang);
  const relatedPosts = data.relatedPosts?.edges || [];

  const metaData = {
    en: {
      title: `${post.frontmatter.title} — TriDyme Blog`,
      description: post.frontmatter.description || post.excerpt
    },
    fr: {
      title: `${post.frontmatter.title} — Blog TriDyme`,
      description: post.frontmatter.description || post.excerpt
    }
  };

  const getAuthorInitials = (author) => {
    if (!author) return 'TD';
    return author.split(' ').map(n => n[0]).join('');
  };

  const shareUrl = `https://www.tridyme.com${post.fields.slug}`;


  return (
    <Layout locale={locale}>
      <HeadMeta locale={locale} metaData={metaData} />

      <div className="article-page">
        {/* Article Header */}
        <header className="article-header">
          <div className="article-header-container">
            <nav className="article-breadcrumb">
              <LocalizedLink to="/">
                {locale === 'fr' ? 'Accueil' : 'Home'}
              </LocalizedLink>
              <span className="separator">›</span>
              <LocalizedLink to="/blog/">Blog</LocalizedLink>
              <span className="separator">›</span>
              <span>{locale === 'fr' ? 'Article' : 'Article'}</span>
            </nav>

            <span className="article-tag">Article</span>

            <h1>{post.frontmatter.title}</h1>

            {post.frontmatter.description && (
              <p className="article-excerpt">{post.frontmatter.description}</p>
            )}

            <div className="article-meta">
              <div className="article-author">
                <div className="author-avatar">
                  {getAuthorInitials(post.frontmatter.author)}
                </div>
                <div className="author-info">
                  <span className="author-name">{post.frontmatter.author || 'TriDyme'}</span>
                  <span className="author-role">TriDyme Team</span>
                </div>
              </div>
              <div className="article-details">
                <span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  {post.frontmatter.date}
                </span>
                <span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  5 min
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="article-featured-image">
          {post.frontmatter.featuredImage ? (
            <img
              src={post.frontmatter.featuredImage.publicURL}
              alt={post.frontmatter.title}
            />
          ) : (
            <div className="placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
              </svg>
            </div>
          )}
        </div>

        {/* Article Content */}
        <article className="article-content">
          <div dangerouslySetInnerHTML={{ __html: html }} />


          {/* Share */}
          <div className="article-share">
            <span>{locale === 'fr' ? 'Partager :' : 'Share:'}</span>
            <div className="share-buttons">
              <LinkedinShareButton url={shareUrl} className="share-btn">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </LinkedinShareButton>
              <TwitterShareButton url={shareUrl} className="share-btn">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </TwitterShareButton>
              <EmailShareButton url={shareUrl} className="share-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <path d="M22 6l-10 7L2 6"/>
                </svg>
              </EmailShareButton>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedPosts.length > 0 && (
          <section className="related-articles">
            <div className="related-container">
              <div className="related-header">
                <h2>
                  <FormattedMessage id="article.relatedArticles" defaultMessage="Articles similaires" />
                </h2>
              </div>
              <div className="related-grid">
                {relatedPosts.slice(0, 3).map(({ node }) => (
                  <article className="related-card" key={node.id}>
                    <LocalizedLink to={node.fields.slug} className="related-card-link">
                      <div className="related-card-image">
                        {node.frontmatter.featuredImage ? (
                          <img
                            src={node.frontmatter.featuredImage.publicURL}
                            alt={node.frontmatter.title}
                          />
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                          </svg>
                        )}
                      </div>
                      <div className="related-card-content">
                        <span className="article-tag">Article</span>
                        <h3>{node.frontmatter.title}</h3>
                        <div className="article-details">
                          <span>{node.frontmatter.date}</span>
                          <span>•</span>
                          <span>5 min</span>
                        </div>
                      </div>
                    </LocalizedLink>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Newsletter CTA */}
        <section className="newsletter-cta">
          <div className="newsletter-container">
            <h2>
              <FormattedMessage id="article.newsletter.title" defaultMessage="Restez informé" />
            </h2>
            <p>
              <FormattedMessage
                id="article.newsletter.description"
                defaultMessage="Recevez nos derniers articles et actualités directement dans votre boîte mail."
              />
            </p>
            <form className="newsletter-form">
              <input
                type="email"
                placeholder={locale === 'fr' ? 'Votre adresse email' : 'Your email address'}
                required
              />
              <button type="submit">
                <FormattedMessage id="article.newsletter.subscribe" defaultMessage="S'abonner" />
              </button>
            </form>
          </div>
        </section>

      </div>
    </Layout>
  );
};

BlogPost.propTypes = {
  pageContext: PropTypes.shape({
    locale: PropTypes.string.isRequired
  }).isRequired
};

export default BlogPost;

export const query = graphql`
  query($slug: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      excerpt
      frontmatter {
        title
        language
        date(formatString: "DD MMMM YYYY", locale: "fr")
        author
        description
        featuredImage {
          publicURL
        }
      }
      fields {
        slug
      }
    }
    relatedPosts: allMarkdownRemark(
      filter: {
        fileAbsolutePath: {regex : "/blog/"},
        fields: { slug: { ne: $slug } }
      },
      sort: { fields: [frontmatter___date], order: DESC },
      limit: 3
    ) {
      edges {
        node {
          id
          frontmatter {
            title
            date(formatString: "DD MMMM YYYY", locale: "fr")
            featuredImage {
              publicURL
            }
          }
          fields {
            slug
          }
        }
      }
    }
  }
`;
